import React, { createContext, useContext, useId, useMemo, useState } from 'react';
import { AvTooltip, FeatureFlags } from '@fabric-ui/components';
import { FormControlLabel, Switch } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useAvContext } from '../../context/AvContextProvider';

const HistoricalBasicModeDebugContext = createContext({ enabled: false, scope: '', toggle: () => {} });

export const HistoricalBasicModeDebugProvider = ({ children }: React.PropsWithChildren) => {
  const [enabled, setEnabled] = useState(false);
  const scope = useId();
  const queryClient = useQueryClient();
  const value = useMemo(
    () => ({
      enabled,
      scope,
      toggle: () => {
        queryClient.invalidateQueries({
          predicate: query => query.meta?.historicalBasicModeDebugScope === scope,
          refetchType: 'none',
        });
        setEnabled(current => !current);
      },
    }),
    [enabled, scope, queryClient]
  );
  return <HistoricalBasicModeDebugContext.Provider value={value}>{children}</HistoricalBasicModeDebugContext.Provider>;
};

export const useHistoricalBasicModeDebug = () => {
  const { featureFlags } = useAvContext();
  const { enabled, scope, toggle } = useContext(HistoricalBasicModeDebugContext);
  const isAvailable = Boolean(featureFlags[FeatureFlags.QueryAdmin]);
  const isEnabled = isAvailable && enabled;
  return {
    isAvailable,
    isEnabled,
    toggle,
    queryOptions: isAvailable && scope ? { meta: { historicalBasicModeDebugScope: scope }, refetchOnMount: true } : {},
    patchQuery: <T extends object>(query: T): T => (isEnabled ? { ...query, historicalBasicMode: true } : query),
  };
};

export const HistoricalBasicModeDebugToggle = () => {
  const { isAvailable, isEnabled, toggle } = useHistoricalBasicModeDebug();
  return isAvailable ? (
    <AvTooltip title="Admin: Basic Historical Mode. This setting is not saved and will reset on page reload.">
      <FormControlLabel
        label="History mode debug"
        labelPlacement="start"
        className="m-0 whitespace-nowrap"
        control={
          <Switch inputProps={{ 'aria-label': 'History mode debug', role: 'switch' }} size="small" checked={isEnabled} onChange={toggle} />
        }
      />
    </AvTooltip>
  ) : null;
};
